import { connect } from '@vfde-sails/core';
import { Device, Option, ProductContainerId, ProductType } from '../../enum';
import { mountSummaryCard, updateSummaryCardButton, updateSummaryCardContent, updateSummaryCardPrices } from '../../components/summaryCard';
import { mountOptionPicker, updateOptionPickerSelection, updateProductPicker } from '../../components/optionPicker';
import { mountUnorderedVerticalList, updateVerticalList } from '../../components/verticalList';
import { setOverlayVisibility, setSelectedProduct } from '../App/action';
import { getUpdateFlags } from './update';
import { mountHookList, updateHookList } from '../../components/hookList';
import { getCMSConfig, getDeviceBenefits, getProductBenefits } from '../../util/cms';
import { mountPromotionalCard, updatePromotionalCard, updatePromotionalCardLink } from '../../components/promotionalCard';
import { selectAvailabilityResult, selectMarketability, selectTvConnect, selectTvConnectBroadvision } from '@vfde-sails/availability';
import { createStructuredSelector } from 'reselect';
import { selectAvailability, selectIsBookingDisabled, selectIsNotMonthlyBilling, selectIsTvCustomer } from '../AddressValidation/selector';
import { selectSelected, selectSelectedId, selectProduct, selectError, selectIsGigakombi } from '../App/selector';
import { mountJustText, updateJustText } from '../../components/justText';
import { selectCustomer, selectIsLoggedIn, selectHasTvConnect } from '@vfde-sails/login';

function productContainer (state: ProductContainerProps, actions: ProductContainerActions) {
    const cmsProps = getCMSConfig();

    const setProductType = (value: string) => actions.setSelectedProduct({ type: value as ProductType });
    const setOption = (value: string) => actions.setSelectedProduct({ option: value as Option });
    const setDevice = (value: string) => actions.setSelectedProduct({ device: value as Device });

    const promotionalCard = mountPromotionalCard(ProductContainerId.PROMOTIONAL_CARD);
    const summaryCard = mountSummaryCard(ProductContainerId.SUMMARY_CARD);
    const productPicker = mountOptionPicker(ProductContainerId.PRODUCT_PICKER, setProductType);
    const optionPicker = mountOptionPicker(ProductContainerId.OPTION_PICKER, setOption);
    const devicePicker = mountOptionPicker(ProductContainerId.DEVICE_PICKER, setDevice);
    const productBenefits = mountUnorderedVerticalList(ProductContainerId.PRODUCT_BENEFITS_LIST);
    const optionBenefit = mountJustText(ProductContainerId.OPTION_BENEFIT);
    const deviceBenefits = mountHookList(ProductContainerId.DEVICE_BENEFITS_LIST, getDeviceBenefits(state.selected.device));

    return {
        getDerivedStateFromProps(newProps: ProductContainerProps, oldProps: ProductContainerProps) {
            if (newProps.isError) {
                return;
            }

            const { selected } = newProps;
            const update = getUpdateFlags(newProps, oldProps);

            if (update.productPiker) {
                updateProductPicker(productPicker, newProps);
                updateVerticalList(productBenefits, getProductBenefits(selected.type));
            };

            if (update.optionPicker) {
                updateOptionPickerSelection(optionPicker, selected.option);
                updateJustText(optionBenefit, cmsProps.optionBenefits[selected.option]);
            }

            if (update.devicePicker) {
                updateOptionPickerSelection(devicePicker, selected.device);
                updateHookList(deviceBenefits, getDeviceBenefits(selected.device));
            }

            if (update.summaryCard) {
                updateSummaryCardContent(summaryCard, newProps, cmsProps.summaryCard);
                updateSummaryCardPrices(summaryCard, newProps, cmsProps.summaryCard);
                updateSummaryCardButton(summaryCard, newProps, cmsProps.summaryCard, actions);
            }

            if (update.promotionalCard) {
                updatePromotionalCard(promotionalCard, newProps, cmsProps.promotionalCard);
                updatePromotionalCardLink(actions.setOverlayVisibility);
            }
        },
    };
}

const actionMap = {
    setOverlayVisibility,
    setSelectedProduct,
};

export const mapStateToProps = createStructuredSelector({
    isError: selectError,
    selected: selectSelected,
    selectedId: selectSelectedId,
    product: selectProduct,
    tvConnect: selectTvConnect,
    tvConnectBv: selectTvConnectBroadvision,
    availabilityResult: selectAvailabilityResult,
    marketability: selectMarketability,
    productAvailability: selectAvailability,
    isGigakombi: selectIsGigakombi,
    customer: selectCustomer,
    isLoggedIn: selectIsLoggedIn,
    isBookingDisabled: selectIsBookingDisabled,
    isNonMonthlyBilling: selectIsNotMonthlyBilling,
    hasTvContract: selectIsTvCustomer,
    hasTvConnect: selectHasTvConnect,
});

export type ProductContainerProps = ReturnType<typeof mapStateToProps>;
export type ProductContainerActions = typeof actionMap;

export default connect(mapStateToProps, actionMap)(productContainer);
